import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SupplierState } from '../../profile/store/state/supplierstate.state';
import { Store, select } from '@ngrx/store';
import { Profile, Location, OwnershipType, Supplier } from '../models/profile.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectCurrentUser, SelectSupplierProfile, SelectSupplierContactList, getAvailableConfiguration, getGeneralSettings } from '../../profile/store/app.selector';
import { Observable, combineLatest } from 'rxjs';
import { FormControl } from '@angular/forms';
import { CommonService } from '../services/common.service';
import { ProfileService } from '../services/profile.service';
import { GetGeneralSettings, GetSupplierProfileCurrentUser, PushAvailableConfiguration } from '../store/app.actions';
import { UserDetails, Contact } from '../models/user.model';
import { AccessRole, ControlType, TabConfig } from '../profile-tab.directory';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { TslOption } from '@tesla/ng-tds';
import { Status, StatusEnum } from '../models/state.model';
import { StateService } from '../services/state.service';
import { debounceTime, startWith } from 'rxjs/operators';
import { AppSettings } from '../../app.settings';

@Component({
  selector: 'app-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  animations: [
    // Each unique animation requires its own trigger. The first argument of the trigger function is the name
    trigger('rotatedState', [
      state('default', style({ transform: 'rotate(0)' })),
      state('rotated', style({ transform: 'rotate(-180deg)' })),
      transition('rotated => default', animate('1500ms')),
      transition('default => rotated', animate('1500ms'))
    ])
  ]
})
export class DetailsComponent implements OnInit {
  panelOpenState = false;
  rotationAmount = 0;
  state: string = 'default';
  currentTab = "";
  tabList: string[] = [];
  searchSupplier: FormControl = new FormControl('');
  newSupplier = "";
  supplierCode: string;
  deleteTitle: string;
  deleteConfirmation: string;
  panelState: boolean = true;
  profile: Profile = new Profile();
  obLocations$: Observable<Location[]>;
  obOwnerships$: Observable<OwnershipType[]>;
  obSupplierList$: Observable<Supplier[]>;
  primaryContact: string = null;
  profileAdmin: string = null;
  availableFields: string[] = [];
  sqaUrl: string = '';
  ctpatUrl: string = '';
  supplierSOS: string = '';
  supplierSOSEmail: string = '';
  userDetails: UserDetails = null;
  contact: Contact = null;
  tabConfiguration: TabConfig[] = null;
  /** Determines to show additional details content */
  @Input('tsl-showAdditionalDetailsLink') showAdditionalDetailsLink: boolean = false;

  /** Event to trigger when the user has selected a new supplier code */
  @Output('tsl-supplierCodeChanged') supplierCodeChangeEvent: EventEmitter<{ selectedSupplierCode: string }> = new EventEmitter<{ selectedSupplierCode: string }>()
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private commonService: CommonService,
    private service: ProfileService,
    private configuration: AppSettings,
    private store: Store<SupplierState>,
    private stateService: StateService) {
  }

  ngOnInit() {
    if (!this.route.snapshot.params["type"]) {
      this.store.dispatch(new GetGeneralSettings());
    }
    this.store.dispatch(new GetSupplierProfileCurrentUser());
    this.searchSupplier.valueChanges
      .pipe(
        debounceTime(300),
        startWith(' ')
      ).subscribe(val => {
        this.obSupplierList$ = this.filterSupplier(val);
      });
    combineLatest(
      [this.store.pipe(select(SelectCurrentUser)),
      this.route.params])
      .subscribe(([result, params]) => {
        if (result && params) {
          if (params["code"]) {
            this.supplierCode = this.commonService.getSupplierCode(params["code"]);
          }

          this.userDetails = result;
          if (this.userDetails.isSupplier && (this.supplierCode === undefined || this.supplierCode === '')) {
            this.newSupplier = this.userDetails.supplierContact.supplierMaps[0].supplierCode;
          }

          if (this.supplierCode && this.userDetails) {
            this.newSupplier = this.supplierCode;
            this.sqaUrl = this.configuration.SRM_UI_URL + `/sqa`;
            this.ctpatUrl = this.configuration.SRM_UI_URL + `/ctpat`;
            this.loadResources();
          }
        }
      });

    combineLatest([this.store.pipe(select(SelectSupplierProfile)),
    this.store.pipe(select(getGeneralSettings))])
      .subscribe(([res, generalSettings]) => {
        if (generalSettings && generalSettings.profileConfiguration) {
          this.tabConfiguration = generalSettings.profileConfiguration
            .map(x => new TabConfig(x.name, x.type, x.accessRole, !x.supplierTypes ? "*" : x.supplierTypes, !x.supplierCategories ? [] : x.supplierCategories,
              x.tierLevel));
        }

        if (res) {
          this.profile = res;
          const accessrole = this.userDetails.isSupplier ? AccessRole.Supplier : AccessRole.Tesla
          const tabFields: string[] = [];
          const tabFieldConfiguration = this.tabConfiguration
            .filter(x => x.controlType === ControlType.Field
              && (x.role === accessrole || x.role === AccessRole.All));
          tabFieldConfiguration.forEach(tabItem => {
            if (typeof tabItem.supplierTypes === 'string' && tabItem.supplierTypes === "*") {
              tabFields.push(tabItem.name);
            } else if (tabItem.supplierTypes instanceof Array) {
              if (!this.profile.supplierDetails.supplierCategory) {
                this.profile.supplierDetails.supplierCategory = [];
              }

              if (tabItem.supplierTypes.some(x => this.profile.supplierDetails
                .supplierCategory.some(y => y.name === x))) {
                tabFields.push(tabItem.name);
              }
            }
          });
          this.availableFields = tabFields;
          this.store.dispatch(new PushAvailableConfiguration(tabFields));

        }
      }, err => {
        console.error(`Service call error: SelectSupplierProfile. Details: ` + err);
      });
  }

  loadResources(): void {
    this.store.pipe(select(SelectSupplierProfile)).subscribe((res) => {
      if (res) {
        this.profile = res;

        if (this.userDetails.isSupplier && this.userDetails.isTeslaUser) {
          this.store.select((getAvailableConfiguration)).subscribe((result: string[]) => {
            this.availableFields = result;
          });
        }
      }
    }, err => {
      console.error(`Service call error: SelectSupplierProfile. Details: ` + err);
    });


    this.store.pipe(select(SelectSupplierContactList))
      .subscribe((supplierContactResult) => {
        if (supplierContactResult) {
          const primaryContactUser = supplierContactResult.find(x => x.types && x.types.newValue && x.types.newValue.includes('Primary'));
          if (primaryContactUser) {
            this.primaryContact = primaryContactUser.email.newValue;
          }
          const profileAdminContact = supplierContactResult.find(x => x.types && x.types.newValue && x.types.newValue.includes('Profile Admin'));
          if (profileAdminContact) {
            this.profileAdmin = profileAdminContact.email.newValue;
          }
        }
      }, err => {
        console.error(`Service call error: GetSupplierContactListByCode or getEntityListByCode. Details: ` + err);
      });

    if (this.showAdditionalDetailsLink) {
      this.service.getSupplierSOS(this.supplierCode).subscribe(res => {
        this.supplierSOS = res['item1'];
        this.supplierSOSEmail = res['item2'];
      });
    }

  }
  getSupplierWebsite(website: string) {
    if (website && !website.toLowerCase().startsWith('http')) {
      return `https://${website}`;
    }

    return website;
  }

  filterSupplier(value: string): Observable<Supplier[]> {
    const minSearchLength = 2;
    const pageSize = 10;
    const filterValue = value.toLowerCase();
    if (filterValue.length > minSearchLength) {
      return this.service.searchSupplier(filterValue, pageSize);
    }
  }

  supplierScoreRenderer(profile) {
    if (profile.supplierDetails && profile.supplierDetails.supplierCode != null && profile.scoreCard != undefined && profile.scoreCard > 0 && profile.scoreCard != null) {
      return `${this.configuration.WarpeventsUrl}/details?supplierCode=${profile.supplierDetails.supplierCode}`;
    }
    else {
      return '';
    }
  }

  onSelectSupplier(event: TslOption): void {
    this.newSupplier = event.value.supplierCode;
    this.onChangeSupplier();
  }

  onChangeSupplier(): void {
    if (this.newSupplier === this.supplierCode) {
      this.searchSupplier.setValue('');
      return;
    }

    const validatedCode = this.commonService.getSupplierCode(this.newSupplier) || '';
    if (validatedCode === '') {
      this.stateService.currentStatus.next(new Status(StatusEnum.InvalidSupplier, ''));
    }

    this.searchSupplier.setValue('');

    this.router.navigate(this.commonService.getRouteFromSupplierState(this.userDetails, validatedCode));

    //  Trigger event that the selected supplier code has changed
    this.supplierCodeChangeEvent.emit({ selectedSupplierCode: this.newSupplier });

  }

  openDetails() {
    this.panelOpenState = !this.panelOpenState;
  }
}
